import {useMemo} from 'react';
import {useAuthStore} from '@common-web/stores/authStore';

/**
 * Whether the signed-in user may see a member's CleverBenefits app-user record.
 * Internal users always can; client/group users need a QUANTUM, HEALTHCUES, or
 * CHAMP affiliation, or the member must be a Synrgy member.
 */
export function useHasAppUserAffiliation(
  enrollments: ReadonlyArray<{groupId: string | null}> | undefined,
): boolean {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const user = useAuthStore((state) => state.user);

  // TODO: Synrgy detection by group prefix needs to be fixed. Tracked in Asana ticket https://app.asana.com/1/1206214889537900/project/1206881612175705/task/1219223869856398?focus=true
  // Synrgy affiliations don't surface as an affiliation string on the client
  // profile (unlike Quantum/HealthCues/CHAMP), so detect Synrgy members from
  // their VBA group prefix (SY) once the member's enrollments have loaded.
  const isSynrgyMember = useMemo(
    () => (enrollments ?? []).some((e) => (e.groupId ?? '').toUpperCase().startsWith('SY')),
    [enrollments],
  );

  return useMemo(() => {
    if (isInternalUser) return true;
    const clientAffiliations = user?.clientProfile?.affiliations ?? [];
    const groupAffiliations = user?.groupProfiles?.flatMap((gp) => gp.affiliations) ?? [];
    const allAffiliations = [...clientAffiliations, ...groupAffiliations];
    const hasAffiliationString = allAffiliations.some(
      (a) => a?.includes('QUANTUM') || a?.includes('HEALTHCUES') || a?.includes('CHAMP'),
    );
    return hasAffiliationString || isSynrgyMember;
  }, [isInternalUser, user?.clientProfile?.affiliations, user?.groupProfiles, isSynrgyMember]);
}
